/**
 * SettingsPage — `/settings`. Currently houses:
 *   • Background theme picker — per-logged-in-account customisation
 *     persisted to `users.preferences.theme_bg` server-side. Visitors
 *     (anonymous) get the default; this page redirects them to /dashboard.
 *
 * Future sections (placeholders): metronome volume, default DAW key,
 * notification prefs, etc.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, Palette, Settings, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import useAuth from "@/lib/useAuth";
import { applyThemeBg, BG_PRESETS, DEFAULT_BG } from "@/lib/themeBg";
import CreatorShell from "@/components/creator/CreatorShell";

export default function SettingsPage() {
  const { user, loading, updatePreferences } = useAuth();
  const navigate = useNavigate();

  const initialBg = user?.preferences?.theme_bg || DEFAULT_BG;
  const [selectedBg, setSelectedBg] = useState(initialBg);
  const [customHex, setCustomHex] = useState(
    initialBg.startsWith("#") ? initialBg : "#0a0814"
  );
  const [saving, setSaving] = useState(false);

  // Live preview — applies the picked color WITHOUT saving so the user can
  // try options before committing.
  useEffect(() => {
    applyThemeBg(selectedBg);
  }, [selectedBg]);

  // iter-83 fix: re-sync the active tile selection when useAuth's /me probe
  // resolves AFTER first render. Otherwise `initialBg = user?.preferences?.theme_bg`
  // captured `user=null` on mount → active ring stuck on "default" even though
  // ThemeBootstrap applied the right --app-bg.
  useEffect(() => {
    const saved = user?.preferences?.theme_bg;
    if (saved && saved !== selectedBg) {
      setSelectedBg(saved);
      if (saved.startsWith("#")) setCustomHex(saved);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.preferences?.theme_bg]);

  // Reset preview to the saved value when leaving the page without saving.
  useEffect(() => {
    return () => {
      applyThemeBg(user?.preferences?.theme_bg || DEFAULT_BG);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Anonymous visitors can't save prefs — redirect to /dashboard after a tick.
  useEffect(() => {
    if (!loading && !user) {
      toast.message("Sign in to customise your studio", {
        description: "Background themes are saved to your account.",
      });
      const t = setTimeout(() => navigate("/dashboard"), 1600);
      return () => clearTimeout(t);
    }
  }, [loading, user, navigate]);

  const isCustom = useMemo(() => selectedBg?.startsWith("#"), [selectedBg]);

  const saveTheme = async () => {
    setSaving(true);
    try {
      await updatePreferences({ theme_bg: selectedBg });
      toast.success("Theme saved", { description: `Background updated for ${user?.email}.` });
    } catch (e) {
      toast.error("Could not save theme", { description: e?.message || "Try again in a moment." });
    } finally {
      setSaving(false);
    }
  };

  const resetTheme = async () => {
    setSelectedBg(DEFAULT_BG);
    setCustomHex("#0a0814");
    setSaving(true);
    try {
      await updatePreferences({ theme_bg: DEFAULT_BG });
      toast.success("Theme reset to default");
    } catch (e) {
      toast.error("Reset failed", { description: e?.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading || !user) {
    return (
      <div className="grid min-h-screen place-items-center bg-[#0a0814] p-8">
        <Settings className="h-6 w-6 animate-spin text-fuchsia-300" />
      </div>
    );
  }

  return (
    <CreatorShell testId="settings-shell" back="/create">
    <div data-testid="settings-page" className="text-white">
      <div className="mx-auto max-w-3xl">
        <Link
          to="/dashboard"
          data-testid="settings-back"
          className="mb-6 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-white/45 transition hover:text-fuchsia-200"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to studio
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
        >
          <h1 className="font-display text-3xl font-semibold">Settings</h1>
          <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-white/45">
            Signed in as {user.email}
          </p>
        </motion.div>

        {/* ─── Background theme ─── */}
        <section
          data-testid="settings-section-bg"
          className="mt-8 rounded-2xl border border-white/[0.08] bg-white/[0.025] p-6 backdrop-blur-xl"
        >
          <div className="mb-2 flex items-center gap-2">
            <Palette className="h-4 w-4 text-fuchsia-300" />
            <h2 className="font-display text-lg font-semibold">App Background</h2>
          </div>
          <p className="font-mono text-[11px] leading-5 text-white/55">
            Picks the studio's background color. Saved to your account — only
            applies on this device when you're signed in.
          </p>

          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
            {BG_PRESETS.map((p) => {
              const active = selectedBg === p.slug;
              return (
                <button
                  key={p.slug}
                  type="button"
                  data-testid={`bg-preset-${p.slug}`}
                  onClick={() => setSelectedBg(p.slug)}
                  className={`group/preset relative aspect-[5/3] overflow-hidden rounded-xl ring-2 transition ${
                    active
                      ? "ring-fuchsia-400 shadow-[0_0_22px_rgba(232,121,249,0.45)]"
                      : "ring-white/10 hover:ring-white/30"
                  }`}
                  style={{ background: p.preview }}
                >
                  <div className="absolute inset-x-2 bottom-2 flex items-center justify-between rounded-md bg-black/55 px-2 py-1 backdrop-blur">
                    <span className="font-mono text-[10px] uppercase tracking-wider text-white">
                      {p.name}
                    </span>
                    {active && <Check className="h-3 w-3 text-fuchsia-200" />}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Custom hex picker */}
          <div className="mt-5 rounded-xl border border-white/[0.06] bg-black/30 p-4">
            <div className="flex flex-wrap items-center gap-3">
              <label
                data-testid="bg-custom-color"
                className={`flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.04] px-2 py-1 ${
                  isCustom ? "ring-1 ring-fuchsia-400/60" : ""
                }`}
              >
                <input
                  type="color"
                  value={customHex}
                  onChange={(e) => {
                    setCustomHex(e.target.value);
                    setSelectedBg(e.target.value);
                  }}
                  className="h-7 w-9 cursor-pointer rounded-md border-0 bg-transparent"
                />
                <span className="font-mono text-[11px] uppercase tracking-wider text-white/65">
                  Custom hex
                </span>
              </label>
              <input
                type="text"
                value={isCustom ? selectedBg : customHex}
                onChange={(e) => {
                  const v = e.target.value;
                  setCustomHex(v);
                  if (/^#[0-9a-fA-F]{0,7}$/.test(v) && v.length >= 4) setSelectedBg(v);
                }}
                maxLength={9}
                placeholder="#0a0814"
                data-testid="bg-custom-hex-input"
                className="w-32 rounded-md border border-white/10 bg-black/40 px-2 py-1 font-mono text-xs text-white placeholder:text-white/30 focus:border-fuchsia-400/60 focus:outline-none"
              />
              <span className="font-mono text-[9.5px] uppercase tracking-wider text-white/35">
                Live preview — hit Save to keep
              </span>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-2">
            <button
              type="button"
              data-testid="bg-save"
              onClick={saveTheme}
              disabled={saving || selectedBg === user.preferences?.theme_bg}
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-fuchsia-500 to-rose-500 px-5 py-2 font-mono text-[11px] uppercase tracking-wider text-white shadow-[0_0_16px_rgba(232,121,249,0.4)] transition hover:opacity-95 disabled:opacity-50"
            >
              {saving ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
              Save background
            </button>
            <button
              type="button"
              data-testid="bg-reset"
              onClick={resetTheme}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-5 py-2 font-mono text-[11px] uppercase tracking-wider text-white/65 transition hover:border-white/25 hover:text-white"
            >
              Reset to default
            </button>
          </div>
        </section>
      </div>
    </div>
    </CreatorShell>
  );
}
